<template>
  <NodeViewWrapper
    as="span"
    class="p-1 inline-block -mb-[16px] min-h-[34px] blank-string"
    :dir="node.attrs.locale === 'he' ? 'rtl' : 'ltr'"
  >
    <VueDraggable
      v-if="format === 'dnd'"
      v-model="model"
      :group="getDragGroup"
      item-key="id"
      :sort="false"
      class="min-w-30 h-[34px] flex flex-col border rounded-md transition"
      :class="{
        'active bg-primary-100 border-primary-600': isActive,
        'border-secondary-250 bg-secondary-100': !isActive,
      }"
      @dragover="isActive = true"
      @dragenter="isActive = true"
      @dragleave="isActive = false"
      @dragstart="onDragStart"
      @dragend="onDragEnd"
      @add="isActive = false"
      @start="onDragStart"
      @end="onDragEnd"
      @drop="onDragEnd"
    >
      <template #item="{ index }">
        <div
          class="h-8 flex items-center bg-white rounded-md px-2 gap-1 hover:cursor-pointer"
          @drag="onDragMove"
        >
          <button @click="remove(index)">
            <CloseIcon class="h-4" />
          </button>
          <p>
            {{ model[index] }}
          </p>
        </div>
      </template>
    </VueDraggable>
    <BaseDropdown
      v-if="format === 'select'"
      fixed
      placement="bottom"
      :text="model[0]"
    >
      <template #trigger>
        <BaseButton color="dark" outline class="h-[34px] min-w-30 !rounded-md">
          <div
            class="flex gap-2 w-full"
            :class="{
              'justify-between': model[0]?.length,
              'justify-end': !model[0]?.length,
            }"
          >
            <div class="grow">{{ model[0] }}</div>
            <ChevronDown :size="16" class="self-end" />
          </div>
        </BaseButton>
      </template>
      <ListGroup>
        <ListGroupItem
          v-for="(option, optionIndex) in options[node.attrs.id]"
          :key="optionIndex"
          :dir="node.attrs.locale === 'he' ? 'rtl' : 'ltr'"
          @click="model[0] = option"
        >
          {{ option }}
        </ListGroupItem>
      </ListGroup>
    </BaseDropdown>

    <div
      v-else-if="format === 'input'"
      class="relative inline-block min-w-[100px] max-w-[300px] overflow-hidden -mb-[10px]"
    >
      <span
        ref="mirror"
        class="invisible whitespace-pre p-2 overflow-hidden max-w-[300px]"
        aria-hidden="true"
      >
        {{ model[0] || ' ' }}
      </span>
      <input
        type="text"
        v-model="model[0]"
        class="absolute start-0 top-1.5 h-[30px] rounded-lg bg-secondary-75 p-2 caret-secondary-900 focus:ring-0 inline-block"
        :style="{ width: `${textWidth}px` }"
      />
    </div>
  </NodeViewWrapper>
</template>

<script setup lang="ts">
import { NodeViewWrapper, nodeViewProps } from '@tiptap/vue-3';
import 'katex/dist/katex.min.css';
import { computed, inject, nextTick, onMounted, ref, watch, Ref } from 'vue';
import VueDraggable from 'vuedraggable';
import { ChevronDown, X as CloseIcon } from 'lucide-vue-next';
import { scrollParent } from '@/utils.ts';
import BaseDropdown from '@/components/Dropdown/BaseDropdown.vue';
import ListGroupItem from '@/components/ListGroup/components/ListGroupItem/ListGroupItem.vue';
import ListGroup from '@/components/ListGroup/ListGroup.vue';
import BaseButton from '@/components/Button/BaseButton.vue';

const props = defineProps(nodeViewProps);
const dragging = ref(false);
const isActive = ref(false);
const format = inject('format');
const options = inject<Record<string, any[]>>('options', {});
const removeElement =
  inject<(index: number, id: string) => void>('removeElement');
const textWidth = ref(30);
const mirror = ref();

const updateWidth = () => {
  if (format !== 'input') return;
  nextTick(() => {
    textWidth.value = Math.min(Math.max(mirror.value?.offsetWidth, 100), 300);
  });
};

const parentModel = inject<Ref<Record<string, any>> | undefined>('model');
const model = computed({
  get: () => parentModel?.value?.[props.node.attrs.id],
  set: val => {
    if (parentModel?.value) {
      parentModel.value = {
        ...parentModel.value,
        [props.node.attrs.id]: val,
      };
    }
  },
});

watch(
  () => props.node.attrs.model,
  newValue => {
    model.value = Array.isArray(newValue) ? [...newValue] : [];
    updateWidth();
  },
  { deep: true, immediate: true },
);

const onDragMove = event => {
  scrollParent(document.querySelector('section.present > .r-stack'), event);
};

const onDragStart = () => {
  dragging.value = true;
};
const onDragEnd = () => {
  dragging.value = false;
  isActive.value = false;
};

const getDragGroup = computed(() =>
  model.value?.length < 1 || dragging.value
    ? 'fill_in_blanks_answers'
    : `answer${props.node.attrs.id}`,
);

const remove = index => {
  model.value.splice(index, 1);
  if (removeElement) {
    removeElement(index, props.node.attrs.id);
  }
};

watch(model, updateWidth, { deep: true, immediate: true });
onMounted(updateWidth);
</script>
